import {dataTestId} from '@hconnect/uikit'
import {Delete, Edit} from '@mui/icons-material'
import {Box, Menu, MenuItem, Typography, useMediaQuery, useTheme} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {useStrengthMeasurementUnit} from '../hooks/useStrengthMeasurementUnit'
import {useStrengthTargetListUtils} from '../hooks/useStrengthTargetListUtils'
import {StrengthTarget, DayPeriod} from '../types'
import {canModifyTarget, formatISODateForDisplay} from '../utils/dateUtils'

import {StrengthTargetListDesktopTable} from './StrengthTargetListDesktopTable'
import {StrengthTargetListMobileCards} from './StrengthTargetListMobileCards'

import {useConfirmDialog} from '@settings/modules/common/providers/ConfirmDialogProvider'

interface StrengthTargetListProps {
  targets: StrengthTarget[]
  dayPeriod: DayPeriod
  timezone: string
  onEditTarget: (target: StrengthTarget) => void
  onDeleteTarget: (target: StrengthTarget) => void
  isReadOnly: boolean
}

export const StrengthTargetList: React.FC<StrengthTargetListProps> = ({
  targets,
  dayPeriod,
  timezone,
  onEditTarget,
  onDeleteTarget,
  isReadOnly
}) => {
  const {t} = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const {openDialog} = useConfirmDialog()

  const unit = useStrengthMeasurementUnit(dayPeriod)

  const sortedTargets = useMemo(() => {
    return [...targets].sort((a, b) => {
      if (!a.validFrom) return 1
      if (!b.validFrom) return -1
      return b.validFrom.localeCompare(a.validFrom)
    })
  }, [targets])

  const {anchorEl, selectedTarget, handleMenuOpen, handleMenuClose, formatDate, formatValidFrom} =
    useStrengthTargetListUtils(timezone)

  const handleCommentClick = (target: StrengthTarget) => {
    const targetLabel =
      target.target !== undefined
        ? `${t('materialsSettings.strengthTargets.target')}: ${target.target}${
            unit ? ` ${unit}` : ''
          }`
        : t('materialsSettings.strengthTargets.targetDetail')

    openDialog({
      title: targetLabel,
      description: `${t('materialsSettings.strengthTargets.validFrom')}: ${formatISODateForDisplay(
        target.validFrom,
        timezone
      )}`,
      additionalContent: (
        <Box
          sx={{
            p: 2,
            backgroundColor: (theme) => theme.palette.grey[50],
            borderRadius: 1,
            border: (theme) => `1px solid ${theme.palette.divider}`
          }}
          {...dataTestId('comment_text_container')}
        >
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: (theme) => theme.palette.text.primary
            }}
            {...dataTestId('comment_text')}
          >
            {target.comment}
          </Typography>
        </Box>
      ),
      mainAction: {
        text: t('common.close')
      },
      showCancelButton: false,
      testId: 'strength_target_comment_dialog'
    })
  }

  const handleEdit = () => {
    if (selectedTarget) {
      onEditTarget(selectedTarget)
    }
    handleMenuClose()
  }

  const handleDelete = () => {
    if (selectedTarget) {
      onDeleteTarget(selectedTarget)
    }
    handleMenuClose()
  }

  return (
    <Box {...dataTestId('strength_target_list')}>
      {isMobile ? (
        <StrengthTargetListMobileCards
          targets={sortedTargets}
          unit={unit}
          isReadOnly={isReadOnly}
          timezone={timezone}
          onEditTarget={onEditTarget}
          onMenuOpen={handleMenuOpen}
          onCommentClick={handleCommentClick}
          formatDate={formatDate}
          formatValidFrom={formatValidFrom}
        />
      ) : (
        <StrengthTargetListDesktopTable
          targets={sortedTargets}
          unit={unit}
          isReadOnly={isReadOnly}
          timezone={timezone}
          onEditTarget={onEditTarget}
          onMenuOpen={handleMenuOpen}
          onCommentClick={handleCommentClick}
          formatDate={formatDate}
          formatValidFrom={formatValidFrom}
        />
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        {...dataTestId('target_action_menu')}
      >
        <MenuItem onClick={handleEdit} {...dataTestId('edit_target_menu_item')}>
          <Edit sx={{mr: 1, fontSize: 20, color: (theme) => theme.palette.primary.main}} />
          {t('common.edit')}
        </MenuItem>
        {selectedTarget &&
          canModifyTarget(selectedTarget.id, selectedTarget.validFrom, timezone) && (
            <MenuItem
              onClick={handleDelete}
              sx={{color: (theme) => theme.palette.error.main}}
              {...dataTestId('delete_target_menu_item')}
            >
              <Delete sx={{mr: 1, fontSize: 20}} />
              {t('common.delete')}
            </MenuItem>
          )}
      </Menu>
    </Box>
  )
}
